import { useCallback, useEffect, useState } from 'react';

import { PageTitle } from '@components/PageTitle';
import { Button } from '@components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { useAuth } from '@contexts/auth';
import formatterDecimal from '@formatters/decimal';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@lib/ui/cn';
import { useForm } from 'react-hook-form';
import { NumberFormatBase } from 'react-number-format';
import { Navigate } from 'react-router';
import { z } from 'zod';

import { useHidratacaoPediatria } from './context';
import { formSchema } from './formSchema';
import { HypotonicSolution } from './HypotonicSolution';
import { IsotonicSolution } from './IsotonicSolution';

enum ETab {
  HypotonicSolution = 'SORO_HIPOTONICO',
  IsotonicSolution = 'SORO_ISOTONICO',
}

function PediatricHydration() {
  const { user } = useAuth();
  const { KCl, mEq, NaCl, setWeight, weight } = useHidratacaoPediatria();
  const [tabEnabled, setTabEnabled] = useState<ETab>(ETab.HypotonicSolution);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    shouldUnregister: true,
    defaultValues: {
      weight: weight.toFixed(1),
      NaCl: NaCl.toFixed(1),
      KCl: KCl.toFixed(1),
      mEq: mEq.toFixed(1),
    },
  });

  const handleChangeTab = useCallback((tab: ETab): void => {
    setTabEnabled(tab);
  }, []);

  const handleChangeWeight = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setWeight(valueParsed);
    },
    [setWeight],
  );

  function onSubmit(): void {
    //
  }

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'weight' && value.weight) {
        handleChangeWeight(value.weight);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  if (!user?.accessGranted) {
    return <Navigate replace to="/pagamento" />;
  }

  return (
    <div>
      <PageTitle hasBackButton title="Hidratação Pediatria (Holliday)" />
      <Form {...form}>
        <form
          className="mx-auto max-w-3xl"
          onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mb-4 flex flex-row justify-center gap-6">
            <FormField
              control={form.control}
              name="weight"
              render={({ field: { ref, ...rest } }) => (
                <FormItem>
                  <FormLabel>Peso (Kg)</FormLabel>
                  <FormControl>
                    <NumberFormatBase
                      className="[&_input]:w-26 [&_input]:text-center"
                      customInput={Input}
                      format={(value) => formatterDecimal(value, 1)}
                      getCaretBoundary={(formattedValue) => {
                        const boundaryAry = Array.from({
                          length: formattedValue.length + 1,
                        }).map(() => true);
                        boundaryAry[0] = false;
                        boundaryAry[1] = false;
                        boundaryAry[2] = false;
                        return boundaryAry;
                      }}
                      getInputRef={ref}
                      {...rest}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="mb-4 flex flex-row justify-center gap-4">
            <Button
              className={cn(
                'border-tertiary hover:text-background hover:bg-tertiary cursor-pointer',
                tabEnabled === ETab.HypotonicSolution &&
                  'bg-tertiary text-background',
              )}
              onClick={() => {
                handleChangeTab(ETab.HypotonicSolution);
              }}
              type="button"
              variant="outline">
              Hipotônico
            </Button>
            <Button
              className={cn(
                'border-tertiary hover:text-background hover:bg-tertiary cursor-pointer',
                tabEnabled === ETab.IsotonicSolution &&
                  'bg-tertiary text-background',
              )}
              onClick={() => {
                handleChangeTab(ETab.IsotonicSolution);
              }}
              type="button"
              variant="outline">
              Isotônico
            </Button>
          </div>

          {tabEnabled === ETab.HypotonicSolution && <HypotonicSolution />}
          {tabEnabled === ETab.IsotonicSolution && <IsotonicSolution />}

          <p className="text-foreground mt-8 text-center text-base font-light">
            <span className="text-lg font-bold">Referência:</span>
          </p>
          <p className="text-foreground mt-4 text-center text-xs font-light">
            Holliday MA, Segar WE. The maintenance need for water in parenteral
            fluid therapy. Pediatrics. 1957;19:823-32.
          </p>
          <p className="text-foreground mt-4 text-center text-xs font-light">
            Recomendações para uso de fluidoterapia isotônica. Sociedade de
            Pediatria de São Paulo. Setembro 2016.
          </p>
        </form>
      </Form>
    </div>
  );
}

export default PediatricHydration;
